import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const RELATIONSHIPS = [
  '本人', '配偶者', '父', '母', '息子', '娘',
  '祖父', '祖母', '孫息子', '孫娘',
  '兄', '姉', '弟', '妹',
  '義父', '義母', '義兄', '義姉', '義弟', '義妹',
  '義祖父', '義祖母',
];


/* ========== タイムピッカー (ドラムロール風) ========== */
const TimePicker = ({ value, onChange, label }) => {
  const [show, setShow] = useState(false);
  const [hour, setHour] = useState(value ? parseInt(value.split(':')[0]) : 12);
  const [minute, setMinute] = useState(value ? parseInt(value.split(':')[1]) : 0);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShow(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const pad = (n) => String(n).padStart(2, '0');

  const handleConfirm = () => {
    onChange(`${pad(hour)}:${pad(minute)}`);
    setShow(false);
  };

  const scrollHandler = (setter, max, current) => (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 1 : -1;
    setter((current + delta + max) % max);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
      <input
        type="text"
        value={value || ''}
        placeholder="時間を選択"
        readOnly
        onClick={() => setShow(!show)}
        style={{ cursor: 'pointer', width: '100%', padding: '12px 16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px' }}
      />
      {show && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 1000,
          background: 'white', border: '1px solid #ddd', borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)', padding: '16px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
          minWidth: '200px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* 時 */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <button type="button" onClick={() => setHour((hour - 1 + 24) % 24)}
                style={arrowStyle}>▲</button>
              <div onWheel={scrollHandler(setHour, 24, hour)}
                style={drumStyle}>{pad(hour)}</div>
              <button type="button" onClick={() => setHour((hour + 1) % 24)}
                style={arrowStyle}>▼</button>
            </div>
            <span style={{ fontSize: '24px', fontWeight: 'bold' }}>:</span>
            {/* 分 */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <button type="button" onClick={() => setMinute((minute - 5 + 60) % 60)}
                style={arrowStyle}>▲</button>
              <div onWheel={scrollHandler(setMinute, 60, minute)}
                style={drumStyle}>{pad(minute)}</div>
              <button type="button" onClick={() => setMinute((minute + 5) % 60)}
                style={arrowStyle}>▼</button>
            </div>
          </div>
          <button type="button" onClick={handleConfirm}
            style={{ background: '#37474f', color: 'white', border: 'none', padding: '8px 24px', borderRadius: '6px', cursor: 'pointer' }}>
            決定
          </button>
        </div>
      )}
    </div>
  );
};

const arrowStyle = {
  background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer',
  padding: '4px 12px', color: '#37474f'
};
const drumStyle = {
  fontSize: '28px', fontWeight: 'bold', width: '60px', textAlign: 'center',
  padding: '8px 0', background: '#f5f5f5', borderRadius: '6px', userSelect: 'none'
};

/* ========== メインフォーム ========== */
const CondolenceCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');  // 編集モード時のID
  const [employees, setEmployees] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!!editId);
  const [savedId, setSavedId] = useState(editId || null);

  const [form, setForm] = useState({
    application_date: new Date().toISOString().split('T')[0],
    flower_ordered: false,
    employee_code: '',
    employee_name: '',
    employee_category: '',
    department: '',
    position: '',
    submitter_name: '',
    send_obituary: '',
    allow_attendance: '',
    request_telegram: '',
    request_flowers: '',
    deceased_name: '',
    deceased_name_kana: '',
    deceased_gender: '',
    deceased_age: '',
    relationship: '',
    death_date: '',
    living_together: '',
    chief_mourner_name: '',
    chief_mourner_name_kana: '',
    chief_mourner_relationship: '',
    deceased_is_employee: '',
    wake_date: '',
    wake_time: '',
    funeral_date: '',
    funeral_time: '',
    venue_name: '',
    venue_postal_code: '',
    venue_address: '',
    venue_phone: '',
    remarks: '',
  });

  useEffect(() => {
    fetch('/api/condolence/check', { credentials: 'include' })
      .then(res => { if (!res.ok) navigate('/condolence'); })
      .catch(() => navigate('/condolence'));

    fetch('/api/condolence/employees', { credentials: 'include' })
      .then(res => res.json())
      .then(data => setEmployees(data.employees || []))
      .catch(err => console.error(err));

    // 編集モード: 既存データを読み込み
    if (editId) {
      fetch(`/api/condolence/applications/${editId}`, { credentials: 'include' })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data) {
            setForm(f => ({
              ...f,
              ...data,
              deceased_age: data.deceased_age !== null && data.deceased_age !== '' ? String(data.deceased_age) : '',
            }));
          }
        })
        .catch(err => console.error(err))
        .finally(() => setLoading(false));
    }
  }, [navigate, editId]);

  const handleEmployeeChange = (e) => {
    const emp = employees.find(emp => emp.employee_code === e.target.value);
    if (emp) {
      setForm(f => ({
        ...f,
        employee_code: emp.employee_code,
        employee_name: emp.name,
        employee_category: emp.employee_category,
        department: emp.department,
        position: emp.position,
      }));
    } else {
      setForm(f => ({
        ...f,
        employee_code: '',
        employee_name: '',
        employee_category: '',
        department: '',
        position: '',
      }));
    }
  };

  const handleKanjiChange = useCallback(async (e) => {
    const name = e.target.value;
    setForm(f => ({ ...f, deceased_name: name }));

    // 漢字→カタカナ変換をバックエンドに依頼（未実装なら手入力）
    if (name.trim()) {
      try {
        const res = await fetch('/api/condolence/convert-kana', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ text: name }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.kana) {
            setForm(f => ({ ...f, deceased_name_kana: data.kana }));
          }
        }
      } catch {
        // 変換失敗時は手入力に任せる
      }
    }
  }, []);

  const handleMournerNameChange = useCallback(async (e) => {
    const name = e.target.value;
    setForm(f => ({ ...f, chief_mourner_name: name }));
    if (name.trim()) {
      try {
        const res = await fetch('/api/condolence/convert-kana', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ text: name }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.kana) {
            setForm(f => ({ ...f, chief_mourner_name_kana: data.kana }));
          }
        }
      } catch {}
    }
  }, []);

  const setField = (field) => (e) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.employee_name && !form.submitter_name) { setError('対象社員名を入力してください'); return; }
    if (!form.employee_category) { setError('社員区分を選択してください'); return; }
    if (!form.deceased_name) { setError('死亡者名を入力してください'); return; }
    if (!form.relationship) { setError('続柄を選択してください'); return; }

    if (!window.confirm('訃報をメール送信します。よろしいですか？')) return;

    setSubmitting(true);
    setError('');

    try {
      // 1. DB保存（新規 or 更新）
      const url = editId
        ? `/api/condolence/applications/${editId}`
        : '/api/condolence/applications';
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const data = await res.json();
        setSavedId(data.id);

        // 2. PDF化してメール送信
        try {
          const sendRes = await fetch(`/api/condolence/send-obituary/${data.id}`, {
            method: 'POST',
            credentials: 'include',
          });
          const sendData = await sendRes.json();
          if (sendData.warning) {
            setError(sendData.warning);
          }
        } catch {
          setError('メール送信に失敗しました');
        }

        setSuccess(true);
      } else {
        const data = await res.json();
        setError(data.error || '申請に失敗しました');
      }
    } catch {
      setError('申請に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="form-page">
      <header className="form-header" style={{ background: '#37474f' }}>
        <button className="back-button" onClick={() => navigate(editId ? '/condolence/history' : '/condolence/menu')}>← 戻る</button>
        <h1>{editId ? '訃報内容の確認・修正' : '訃報作成・供花手配'}</h1>
      </header>

      <main className="form-main">
        <div className="section-bar" style={{ background: '#eceff1' }}>
          <span className="icon">🕯️</span>
          <span className="title" style={{ color: '#37474f' }}>訃報情報入力</span>
        </div>

        {loading && <div style={{ textAlign: 'center', padding: '24px', color: '#666' }}>読み込み中...</div>}
        {success && <div className="success-message">{editId ? '修正を保存しました。' : '訃報を作成しました。'}Excelファイルがダウンロードされます。</div>}
        {error && <div className="error-message">{error}</div>}

        <div className="form-card">
          {/* === 社員選択セクション === */}
          <h3 style={{ marginBottom: '16px', color: '#37474f', borderBottom: '2px solid #37474f', paddingBottom: '8px' }}>
            対象社員情報
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>対象社員<span className="required">*</span></label>
              <input type="text" value={form.submitter_name || form.employee_name}
                onChange={setField('employee_name')} placeholder="社員名" />
            </div>
            <div className="form-group">
              <label>社員区分<span className="required">*</span></label>
              <select value={form.employee_category} onChange={setField('employee_category')}>
                <option value="">選択してください</option>
                <option value="社員">社員</option>
                <option value="社員（出向）">社員（出向）</option>
                <option value="アルバイト">アルバイト</option>
              </select>
            </div>
          </div>

          <hr className="form-divider" />

          {/* === 故人情報セクション === */}
          <h3 style={{ marginBottom: '16px', color: '#37474f', borderBottom: '2px solid #37474f', paddingBottom: '8px' }}>
            故人情報
          </h3>

          <div className="form-group">
            <label>死亡者名<span className="required">*</span></label>
            <input type="text" value={form.deceased_name} onChange={handleKanjiChange} placeholder="氏名を入力" />
          </div>

          <div className="form-group">
            <label>死亡者名フリガナ</label>
            <input type="text" value={form.deceased_name_kana}
              onChange={setField('deceased_name_kana')} placeholder="カタカナで入力" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>性別<span className="required">*</span></label>
              <div className="radio-group">
                {['男', '女'].map(g => (
                  <label key={g} className="radio-option">
                    <input type="radio" name="gender" value={g}
                      checked={form.deceased_gender === g}
                      onChange={setField('deceased_gender')} />
                    {g}
                  </label>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label>享年</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="number" value={form.deceased_age}
                  onChange={setField('deceased_age')} placeholder="例: 85" min="0" max="150"
                  style={{ flex: 1 }} />
                <span>歳</span>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>当社従業員から見た続柄<span className="required">*</span></label>
            <select value={form.relationship} onChange={setField('relationship')}>
              <option value="">選択してください</option>
              {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>死亡年月日</label>
              <input type="date" value={form.death_date} onChange={setField('death_date')} />
            </div>
            <div className="form-group">
              <label>死亡者との同居</label>
              <div className="radio-group">
                {['同居', '別居'].map(v => (
                  <label key={v} className="radio-option">
                    <input type="radio" name="living_together" value={v}
                      checked={form.living_together === v}
                      onChange={setField('living_together')} />
                    {v}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <hr className="form-divider" />

          {/* === 喪主情報セクション === */}
          <h3 style={{ marginBottom: '16px', color: '#37474f', borderBottom: '2px solid #37474f', paddingBottom: '8px' }}>
            喪主情報
          </h3>

          <div className="form-group">
            <label>喪主名</label>
            <input type="text" value={form.chief_mourner_name}
              onChange={handleMournerNameChange} placeholder="喪主名を入力" />
          </div>

          <div className="form-group">
            <label>喪主名フリガナ</label>
            <input type="text" value={form.chief_mourner_name_kana}
              onChange={setField('chief_mourner_name_kana')} placeholder="カタカナで入力" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>死亡者から見た喪主の続柄</label>
              <select value={form.chief_mourner_relationship}
                onChange={setField('chief_mourner_relationship')}>
                <option value="">選択してください</option>
                {['父', '母', '妻', '夫', '長男', '長女', '次男', '次女', '姉', '兄', '妹', '弟', '娘婿'].map(r =>
                  <option key={r} value={r}>{r}</option>
                )}
              </select>
            </div>
            <div className="form-group">
              <label>死亡者の社員有無</label>
              <div className="radio-group">
                {['従業員', '従業員以外'].map(v => (
                  <label key={v} className="radio-option">
                    <input type="radio" name="deceased_is_employee" value={v}
                      checked={form.deceased_is_employee === v}
                      onChange={setField('deceased_is_employee')} />
                    {v}
                  </label>
                ))}
              </div>
            </div>
          </div>

          <hr className="form-divider" />

          {/* === 通夜・葬儀セクション === */}
          <h3 style={{ marginBottom: '16px', color: '#37474f', borderBottom: '2px solid #37474f', paddingBottom: '8px' }}>
            通夜・葬儀日程
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>通夜日程</label>
              <input type="date" value={form.wake_date} onChange={setField('wake_date')} />
            </div>
            <div className="form-group">
              <label>通夜開始時間</label>
              <TimePicker value={form.wake_time}
                onChange={(v) => setForm(f => ({ ...f, wake_time: v }))} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>葬儀日程</label>
              <input type="date" value={form.funeral_date} onChange={setField('funeral_date')} />
            </div>
            <div className="form-group">
              <label>葬儀開始時間</label>
              <TimePicker value={form.funeral_time}
                onChange={(v) => setForm(f => ({ ...f, funeral_time: v }))} />
            </div>
          </div>

          <hr className="form-divider" />

          {/* === 会場セクション === */}
          <h3 style={{ marginBottom: '16px', color: '#37474f', borderBottom: '2px solid #37474f', paddingBottom: '8px' }}>
            会場情報
          </h3>

          <div className="form-group">
            <label>会場名</label>
            <input type="text" value={form.venue_name} onChange={setField('venue_name')} placeholder="会場名を入力" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>郵便番号</label>
              <input type="text" value={form.venue_postal_code}
                onChange={setField('venue_postal_code')} placeholder="000-0000" />
            </div>
            <div className="form-group">
              <label>住所</label>
              <input type="text" value={form.venue_address}
                onChange={setField('venue_address')} placeholder="住所を入力" />
            </div>
          </div>

          <div className="form-group">
            <label>会場電話番号</label>
            <input type="text" value={form.venue_phone}
              onChange={setField('venue_phone')} placeholder="000-0000-0000" />
          </div>

          <hr className="form-divider" />

          {/* === 備考セクション === */}
          <h3 style={{ marginBottom: '16px', color: '#37474f', borderBottom: '2px solid #37474f', paddingBottom: '8px' }}>
            備考
          </h3>

          <div className="form-group">
            <textarea
              value={form.remarks}
              onChange={setField('remarks')}
              placeholder="備考があれば入力してください"
              rows={4}
              style={{ width: '100%', padding: '12px 16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', resize: 'vertical' }}
            />
          </div>

          {/* === 従業員の希望（読み取り専用） === */}
          {(form.send_obituary || form.request_telegram || form.request_flowers) && (
            <>
              <hr className="form-divider" />
              <h3 style={{ marginBottom: '16px', color: '#37474f', borderBottom: '2px solid #37474f', paddingBottom: '8px' }}>
                従業員の希望
              </h3>
              <div style={{
                background: '#f5f5f5', borderRadius: '8px', padding: '16px',
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '24px'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>訃報の共有</div>
                  <div style={{ fontWeight: '600' }}>{form.send_obituary || '未選択'}</div>
                </div>
                {form.allow_attendance && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>社員の参列</div>
                    <div style={{ fontWeight: '600' }}>{form.allow_attendance}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>弔電</div>
                  <div style={{ fontWeight: '600' }}>{form.request_telegram || '未選択'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>供花</div>
                  <div style={{ fontWeight: '600' }}>{form.request_flowers || '未選択'}</div>
                </div>
              </div>
            </>
          )}

          {/* === 送信 === */}
          {(() => {
            const isPartTime = form.employee_category === 'アルバイト';
            const allowedForPartTimeFlower = ['本人', '配偶者'];
            const allowedForPartTimeTelegram = ['本人', '配偶者', '父', '母', '息子', '娘'];
            const flowerDisabledByCategory = isPartTime && !allowedForPartTimeFlower.includes(form.relationship);
            const telegramDisabledByCategory = isPartTime && !allowedForPartTimeTelegram.includes(form.relationship);
            const obituaryDeclined = form.send_obituary === '辞退する';
            const telegramDeclined = form.request_telegram === '辞退する' || form.request_telegram === '辞退';
            const flowerDeclined = form.request_flowers === '辞退する' || form.request_flowers === '辞退';
            const obituaryDisabled = submitting || success;
            const telegramDisabled = telegramDeclined || telegramDisabledByCategory;
            const flowerDisabled = !savedId || flowerDisabledByCategory || flowerDeclined || form.flower_ordered;
            return (
              <>
                {flowerDisabledByCategory && (
                  <div style={{
                    background: '#fce4ec', border: '1px solid #ef9a9a', borderRadius: '8px',
                    padding: '12px 16px', marginBottom: '16px', textAlign: 'center',
                    fontSize: '14px', color: '#c62828', fontWeight: '500'
                  }}>
                    アルバイトの方は本人・配偶者以外の場合、供花の発注対象外です。
                  </div>
                )}
                {telegramDisabledByCategory && (
                  <div style={{
                    background: '#fce4ec', border: '1px solid #ef9a9a', borderRadius: '8px',
                    padding: '12px 16px', marginBottom: '16px', textAlign: 'center',
                    fontSize: '14px', color: '#c62828', fontWeight: '500'
                  }}>
                    アルバイトの方は本人・配偶者・父・母・息子・娘以外の場合、弔電の依頼対象外です。
                  </div>
                )}
                {obituaryDeclined && (
                  <div style={{
                    background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '8px',
                    padding: '12px 16px', marginBottom: '16px', textAlign: 'center',
                    fontSize: '14px', color: '#e65100', fontWeight: '500'
                  }}>
                    提出者が訃報の社内共有を辞退しています。<br />
                    訃報送信は自部署/自店役職者と共済会のみへ縮小して送信されます。
                  </div>
                )}
                {telegramDeclined && (
                  <div style={{
                    background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '8px',
                    padding: '12px 16px', marginBottom: '16px', textAlign: 'center',
                    fontSize: '14px', color: '#e65100', fontWeight: '500'
                  }}>
                    提出者が弔電を辞退しています。
                  </div>
                )}
                {flowerDeclined && (
                  <div style={{
                    background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: '8px',
                    padding: '12px 16px', marginBottom: '16px', textAlign: 'center',
                    fontSize: '14px', color: '#e65100', fontWeight: '500'
                  }}>
                    提出者が供花を辞退しています。
                  </div>
                )}
                <div style={{
                  background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: '8px',
                  padding: '12px 16px', marginBottom: '16px',
                  fontSize: '13px', color: '#0d47a1', lineHeight: 1.7
                }}>
                  ※ 供花発注は1件につき1回までです。<br />
                  内容変更などあれば、9:00〜18:00 <strong>011-642-5758</strong>　それ以外の場合は <strong>090-7518-3843</strong>　ブロックコーポレーション 宮崎様にご連絡をしてください。
                </div>
                <div className="submit-section" style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button className="submit-button" onClick={handleSubmit}
                    disabled={obituaryDisabled}
                    style={{
                      background: obituaryDisabled ? '#bdbdbd' : '#37474f',
                      cursor: obituaryDisabled ? 'not-allowed' : 'pointer'
                    }}>
                    {submitting ? '処理中...' : '訃報送信'}
                  </button>
                  <button className="submit-button"
                    onClick={async () => {
                      window.open('https://www.keicho.net/custom_login.php?lgpt=1', '_blank');
                      if (savedId) {
                        try {
                          await fetch(`/api/condolence/mark-telegram/${savedId}`, {
                            method: 'POST', credentials: 'include'
                          });
                        } catch {}
                      }
                    }}
                    disabled={telegramDisabled}
                    style={{
                      background: telegramDisabled ? '#bdbdbd' : '#1a237e',
                      cursor: telegramDisabled ? 'not-allowed' : 'pointer'
                    }}>
                    弔電依頼
                  </button>
                  <button className="submit-button"
                    onClick={() => navigate(`/condolence/flower?id=${savedId}`)}
                    disabled={flowerDisabled}
                    style={{
                      background: flowerDisabled ? '#bdbdbd' : '#5d4037',
                      cursor: flowerDisabled ? 'not-allowed' : 'pointer'
                    }}>
                    {form.flower_ordered && !flowerDisabledByCategory ? '供花発注済み' : '供花発注'}
                  </button>
                </div>
              </>
            );
          })()}
        </div>
      </main>
    </div>
  );
};

export default CondolenceCreate;
